// Copyright 2025, University of Maryland, All Rights Reserved
import { useQuery } from '@tanstack/react-query';
import { useOrganizationApi, type OrganizationApi } from '../services/organization-api';
import type { OrganizationResponse } from '../types/workspace';

/**
 * Standardized query key for organizations data.
 * Using a consistent key ensures all components share the same cached data.
 */
export const ORGANIZATIONS_QUERY_KEY = ['organizations'];

interface UseOrganizationsOptions {
  /** Optional API instance for testing */
  organizationApi?: OrganizationApi;
}

export function useOrganizations(options: UseOrganizationsOptions = {}) {
  const defaultApi = useOrganizationApi();
  const organizationApi = options.organizationApi ?? defaultApi;

  return useQuery<OrganizationResponse[], Error>({
    queryKey: ORGANIZATIONS_QUERY_KEY,
    queryFn: () => organizationApi.listOrganizations(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
}
